import React, { useState, useEffect, } from 'react';
import { useParams } from 'react-router-dom';
import API_URL from '../config/API_URL';
import { ChevronLeft, ChevronRight, Video, BookOpen, Users, Clock, Heart, Play } from 'lucide-react';

const BookPreviewPage = () => {
  const { bookId } = useParams(); // Get bookId from URL path
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedKidId = urlParams.get('childId');
  const [book, setBook] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [availableKids, setAvailableKids] = useState([]);
  const isPremium = urlParams.get('premium') === 'true';

  const [selectedKids, setSelectedKids] = useState([]);
  const [loadingKids, setLoadingKids] = useState(true);

  // Get book ID from URL

  // Fetch book details
  useEffect(() => {
    const fetchBook = async () => {
      try {
        const premiumParam = isPremium ? '?premium=true' : '';

        const response = await fetch(`${API_URL}/api/books/${bookId}${premiumParam}`, {
          credentials: 'include'
        });
        const data = await response.json();
        setBook(data);
      } catch (error) {
        console.error('Error fetching book:', error);
      } finally {
        setLoading(false);
      }
    };

    if (bookId) {
      fetchBook();
    }
  }, [bookId]);

  // Fetch available kids from all families where user is a reader
  useEffect(() => {
    const fetchKids = async () => {
      try {
        const response = await fetch(`${API_URL}/api/family/my-families`, {
          credentials: 'include'
        });
        const families = await response.json();
        const readerFamilies = families.filter(f => f.my_role === 'reader');
        
        const allKids = [];
        for (const family of readerFamilies) {
          const detailRes = await fetch(`${API_URL}/api/family/${family.id}`, {
            credentials: 'include'
          });
          const detail = await detailRes.json();
          
          if (detail.kids) {
            detail.kids.forEach(kid => {
              allKids.push({
                ...kid,
                familyName: detail.family_name,
                familyId: detail.id
              });
            });
          }
        }
        
        setAvailableKids(allKids);
        
        // Pre-select kid if provided
        if (preselectedKidId) {
          const preselected = allKids.find(k => k.id === parseInt(preselectedKidId));
          if (preselected) {
            setSelectedKids([preselected]);
          }
        }
      } catch (error) {
        console.error('Error fetching kids:', error);
      } finally {
        setLoadingKids(false);
      }
    };

    fetchKids();
  }, [preselectedKidId]);

  const toggleKidSelection = (kid) => {
    setSelectedKids(prev => {
      const isSelected = prev.find(k => k.id === kid.id);
      if (isSelected) {
        return prev.filter(k => k.id !== kid.id);
      } else {
        return [...prev, kid];
      }
    });
  };
  
const handleStartRecording = () => {
  console.log(preselectedKidId);
  const childIdParam = preselectedKidId 
    ? `&childId=${preselectedKidId}` 
    : '';
  const premiumParam = isPremium ? '&premium=true' : '';
  window.location.href = `/record?bookId=${bookId}${childIdParam}${premiumParam}`;
};

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>Loading book...</p>
      </div>
    );
  }

  if (!book) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.25rem', color: '#6b7280', marginBottom: '1rem' }}>Book not found</p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#9333ea',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '1rem 1.5rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button 
              onClick={() => window.location.href = '/grandparent-dashboard'}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              <ChevronLeft size={20} /> Back to Library
            </button>
          </div>
          <button
            onClick={handleStartRecording}
            disabled={selectedKids.length === 0}
            style={{
              padding: '0.75rem 1.5rem',
              background: selectedKids.length > 0 ? '#dc2626' : '#e5e7eb',
              color: selectedKids.length > 0 ? 'white' : '#9ca3af',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: selectedKids.length > 0 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Video size={20} /> Start Recording
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
          {/* Left: PDF Viewer */}
          <div>
            {/* Book Info Card */}
            <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              padding: '1.5rem',
              marginBottom: '1rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #9333ea, #7e22ce)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <BookOpen size={40} color="white" />
                </div>
                <div style={{ flex: 1 }}>
                  <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>
                    {book.title}
                  </h1>
                  <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                    by {book.author}
                  </p>
                  {book.description && (
                    <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.6' }}>
                      {book.description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* PDF Viewer */}
            <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              overflow: 'hidden',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <div style={{ 
                background: '#f3f4f6', 
                padding: '0.75rem 1rem', 
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#4b5563' }}>
                  Preview Book
                </span>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Page {currentPage}
                </span>
              </div>
              
              {book.pdf_url && (
                <iframe 
                src={`${book.pdf_url.trim()}#page=${currentPage}&view=FitH`}
                style={{ 
                  width: '100%', 
                  height: '700px', 
                  border: 'none'
                }}
                title={book.title}
              />
              )}

              {/* Page Navigation */}
              <div style={{ 
                background: '#f9fafb', 
                padding: '1rem', 
                borderTop: '1px solid #e5e7eb',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '1rem'
              }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === 1 ? '#f3f4f6' : '#9333ea',
                    color: currentPage === 1 ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500'
                  }}
                >
                  <ChevronLeft size={20} /> Previous
                </button>
                
                <input
                  type="number"
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page > 0) setCurrentPage(page);
                  }}
                  style={{
                    width: '60px',
                    padding: '0.5rem',
                    textAlign: 'center',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                />

                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#9333ea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500'
                  }}
                >
                  Next <ChevronRight size={20} />
                </button>
              </div>
            </div>

            {/* Attribution */}
            {book.attribution_url && (
              <div style={{ 
                marginTop: '1rem',
                padding: '1rem',
                background: 'white',
                borderRadius: '0.5rem',
                fontSize: '0.75rem',
                color: '#6b7280'
              }}>
                <p style={{ marginBottom: '0.5rem', fontWeight: '600' }}>Attribution:</p>
                <a 
                  href={book.attribution_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#9333ea', textDecoration: 'none' }}
                >
                  View full attribution and license →
                </a>
              </div>
            )}
          </div>

          {/* Right: Kid Selection & Actions */}
          <div>
            {/* Recording Prompt */}
            <div style={{ 
              background: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)', 
              color: 'white',
              borderRadius: '0.75rem', 
              padding: '1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Ready to Record?
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#e9d5ff', marginBottom: '1rem' }}>
                Preview the book, select who you're reading to, then start recording your story.
              </p>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}>
                <Clock size={16} />
                <span>Take your time to practice first!</span>
              </div>
            </div>

            {/* Kid Selection */}
            {/* <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              padding: '1.5rem',
              marginBottom: '1rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                  Recording For
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Select the kids who will receive this recording
                </p>
              </div>

              {selectedKids.length > 0 && (
                <div style={{ 
                  padding: '0.75rem',
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <p style={{ fontSize: '0.875rem', color: '#166534', fontWeight: '600' }}>
                    ✓ {selectedKids.length} {selectedKids.length === 1 ? 'child' : 'children'} selected
                  </p>
                </div>
              )}

              {loadingKids ? (
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading kids...</p>
              ) : availableKids.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem 1rem',
                  background: '#f9fafb',
                  borderRadius: '0.5rem'
                }}>
                  <Users size={32} color="#9ca3af" style={{ margin: '0 auto 0.5rem' }} />
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    No kids available. Connect with families first.
                  </p>
                  <button
                    onClick={() => window.location.href = '/'}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#9333ea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}
                  >
                    Go to Dashboard
                  </button>
                </div>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.75rem',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: '0.5rem'
                }}>
                  {availableKids.map(kid => {
                    const isSelected = selectedKids.find(k => k.id === kid.id);
                    const avatarColors = ['#FF6B9D', '#4ECDC4', '#FFD93D', '#A8E6CF', '#FF8B94', '#C7CEEA'];
                    const avatarColor = avatarColors[kid.id % avatarColors.length];
                    
                    return (
                      <label
                        key={kid.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          border: `2px solid ${isSelected ? '#9333ea' : '#e5e7eb'}`,
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          background: isSelected ? '#faf5ff' : 'white',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => toggleKidSelection(kid)}
                          style={{ 
                            cursor: 'pointer',
                            width: '18px',
                            height: '18px',
                            accentColor: '#9333ea'
                          }}
                        />
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}dd)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.125rem',
                          textTransform: 'uppercase',
                          flexShrink: 0
                        }}>
                          {kid.first_name.charAt(0)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.9375rem' }}>
                            {kid.first_name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {kid.familyName}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div> */}

            {/* Action Buttons */}
            <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              padding: '1.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
                Actions
              </h3>
              
              <button
                onClick={handleStartRecording}
                disabled={false}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: '#dc2626' ,
                  color:'white' ,
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor:'pointer' ,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.75rem'
                }}
              >
                <Video size={20} /> Start Recording Now
              </button>

              <button
                onClick={() => window.location.href = '/'}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'white',
                  color: '#6b7280',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Back to Library
              </button>

              {selectedKids.length === 0 && (
                <p style={{ 
                  fontSize: '0.75rem', 
                  color: '#ef4444', 
                  marginTop: '0.75rem',
                  textAlign: 'center'
                }}>
                  Please select at least one child to record for
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookPreviewPage;